import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, tap } from 'rxjs/operators';
import { ActivityEventDto } from '../../../models/activity.models';
import { ActivityService } from '../../../services/activity.service';
import { resolveMentionLinks } from '../../../utils/mention.utils';

/**
 * The dashboard "Recent Activity" feed: a server-paged table of visibility-filtered
 * activity rows, with entity mention links and live refresh driven by the contentless
 * ActivityFeedUpdated SignalR signal. Deliberately dumb about content: the look-back
 * window, visibility, and ordering are all the API's business.
 */
@Component({
  selector: 'app-activity-feed',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatTableModule,
  ],
  templateUrl: './activity-feed.component.html',
  styleUrls: ['./activity-feed.component.scss'],
})
export class ActivityFeedComponent implements OnInit {
  /**
   * One user action can record several events back to back (a fulfillment fires one
   * broadcast per activation), so refresh signals collapse into a single refetch.
   */
  private static readonly REFRESH_DEBOUNCE_MS = 1000;

  private activityService = inject(ActivityService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  displayedColumns: string[] = ['message', 'createdAt'];
  rows: ActivityEventDto[] = [];
  totalCount = 0;
  page = 1;
  pageSize = 5;
  readonly pageSizeOptions = [5, 10, 25];
  isLoading = false;
  hasError = false;

  private isActive = false;
  private stale = false;

  /** Page numbers to load; every load routes through the switchMap pipeline below. */
  private readonly loadRequest$ = new Subject<number>();

  /**
   * Whether the feed's dashboard tab is the selected one. Refresh signals arriving while
   * inactive only mark the feed stale; the refetch happens on the next activation, so a
   * busy firm doesn't have every open dashboard refetching a tab nobody is looking at.
   */
  @Input() set active(value: boolean) {
    this.isActive = value;
    if (value && this.stale) {
      this.stale = false;
      this.loadPage(1);
    }
  }

  ngOnInit(): void {
    // Every load flows through one switchMap pipeline, so the latest request is the
    // only one that can write to rows: two quick paginator clicks, or the debounced
    // refetch overlapping a user's page change, cancel the in-flight response instead
    // of racing it. catchError sits on the INNER observable so a failure becomes an
    // error state without completing the pipeline.
    this.loadRequest$
      .pipe(
        tap((page) => {
          this.page = page;
          this.isLoading = true;
        }),
        switchMap((page) =>
          this.activityService.getRecentActivity(page, this.pageSize).pipe(
            map((result) => ({ ok: true as const, result })),
            catchError(() => of({ ok: false as const })),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((outcome) => {
        this.isLoading = false;
        if (outcome.ok) {
          this.hasError = false;
          this.rows = outcome.result.items;
          this.totalCount = outcome.result.totalCount;
        } else {
          // A failed load is an error state, never an empty feed — "No recent
          // activity" would be a confident lie when the API was simply unreachable.
          this.hasError = true;
          this.rows = [];
          this.totalCount = 0;
        }
      });

    this.loadPage(1);

    this.activityService.registerFeedUpdates();
    this.activityService.feedUpdated$
      .pipe(
        debounceTime(ActivityFeedComponent.REFRESH_DEBOUNCE_MS),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.isActive) {
          this.loadPage(1);
        } else {
          this.stale = true;
        }
      });
  }

  onPage(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.loadPage(event.pageIndex + 1);
  }

  retry(): void {
    this.loadPage(this.page);
  }

  getDisplayMessage(row: ActivityEventDto): string {
    return resolveMentionLinks(row.message, row.mentions);
  }

  /**
   * Mention links are plain anchors inside [innerHTML]; intercept them so navigation
   * goes through the router instead of a full page load (same handling as the
   * notification list).
   */
  onMessageClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.classList.contains('mention-link')) {
      event.preventDefault();
      const href = target.getAttribute('href');
      if (href) {
        this.router.navigateByUrl(href);
      }
    }
  }

  private loadPage(page: number): void {
    this.loadRequest$.next(page);
  }
}
